import * as React from 'react';
import { Accordion as AccordionPrimitive } from 'radix-ui';

import { cn } from '@april9au/react-common/utils';

import { Icon } from './icon';

function Accordion({ ...props }: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />;
}

function AccordionItem({ className, ...props }: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn(
        'bg-background has-focus-visible:border-ring has-focus-visible:ring-ring/50 rounded-md border border-b px-6 py-1 shadow-sm outline-none last:border-b has-focus-visible:ring-[3px]',
        className,
      )}
      {...props}
    />
  );
}

function AccordionTrigger({ className, children, ...props }: React.ComponentProps<typeof AccordionPrimitive.Trigger>) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          // text-foreground explicitly, not inherited — the item's bg-background
          // is hardcoded navy regardless of the section it sits in, so on a
          // .section--light page (which sets color: navy on the whole section)
          // inherited text would be navy-on-navy and disappear.
          'text-foreground flex w-full flex-1 items-center justify-between rounded-md py-2 text-left text-sm text-[15px] leading-6 font-semibold transition-all outline-none focus-visible:ring-0 [&>svg>path:last-child]:origin-center [&>svg>path:last-child]:transition-all [&>svg>path:last-child]:duration-200 [&[data-state=open]>svg]:rotate-180 [&[data-state=open]>svg>path:last-child]:opacity-100',
          className,
        )}
        {...props}
      >
        {children}

        <Icon
          name="heroicons/chevron-down-micro"
          className="pointer-events-none size-4 shrink-0 opacity-60 transition-transform duration-200"
        />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

function AccordionContent({ className, children, ...props }: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      // text-muted-foreground explicitly, for the same reason as the trigger's
      // text-foreground above — inherited color would go navy-on-navy on a
      // .section--light page.
      className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down text-muted-foreground -mx-1 overflow-hidden px-1 pt-2 pb-4 text-sm"
      {...props}
    >
      <div className={cn('pt-0 pb-4', className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
